import React from "react";
import JournalEntryDrawer from "../../../components/common/jouralEntryDrawer/journalEntryDrawer";
import { Link, Typography } from "@mui/material";
import useLogAccessEvent from "../../../hooks/useLogAccessEvent";
import { AccessEventRegistrationDetails } from "../../../services/types/accessEventTypes";
import { useSearchJournalEntriesMutation } from "../../../services/cclTokenedJournalEntryApi";
import { JournalEntry } from "../../../services/types/journalEntryTypes";
import { Participant } from "../../../services/types/enterpriseParticipantApiTypes";

export type RegistrationJournalDrawerProps = {
    registrations: Participant[];
    sessionCode: string;
    showCountBanner: boolean;
    open: boolean;
    onClose: () => void;
    onSave: () => void;
};

const RegistrationJournalDrawer: React.FC<RegistrationJournalDrawerProps> = (props) => {
    const { logEvent } = useLogAccessEvent();
    const [allJournalEntries, setAllJournalEntries] = React.useState<JournalEntry[]>([]);
    const [runSearchJournalEntries] = useSearchJournalEntriesMutation();

    // r.sessionCode is not reliably populated by the participant endpoints, so the
    // session-level sessionCode (known good, already used by the Session Journal search)
    // is passed in explicitly rather than trusting the per-registration field.
    const catData = props.registrations.map((r) => {
        return {
            esiKey: r.esiKey.toString(),
            imKey: r.imKey.toString(),
            sessionCode: r.sessionCode || props.sessionCode,
        };
    });

    const singleEsiKey =
        props.registrations.length === 1 ? props.registrations[0].esiKey.toString() : undefined;

    React.useEffect(() => {
        if (!props.open || singleEsiKey === undefined) return;
        let ignore = false;
        let entries: JournalEntry[] = [];
        setAllJournalEntries([]);

        runSearchJournalEntries({
            journalCategoryIds: [3],
            categoryDataFieldName: "esiKey",
            categoryDataFieldValue: singleEsiKey,
        })
            .unwrap()
            .then((journalentries: JournalEntry[]) => {
                if (ignore) return;
                entries = [...journalentries];
                setAllJournalEntries(entries);
            })
            .catch(() => {
                if (ignore) return;
                setAllJournalEntries(entries);
            });

        return () => {
            ignore = true;
        };
    }, [props.open, singleEsiKey, runSearchJournalEntries]);

    function saveEntry() {
        props.registrations.forEach((r) => {
            const evtData: AccessEventRegistrationDetails = {
                esiKey: r.esiKey.toString(),
                imKey: r.imKey.toString(),
                email: r.emailAddress,
            };
            logEvent("RegistrationJournalEntryCreated", evtData);
        });

        props.onSave();
    }

    return (
        <JournalEntryDrawer
            bannerElement={
                props.showCountBanner ? (
                    <Typography sx={{ padding: 2 }}>
                        <span style={{ fontWeight: "bold" }}>{props.registrations.length}</span>{" "}
                        participants selected
                    </Typography>
                ) : undefined
            }
            journalCategory={3}
            journalCategoryData={catData.map((cd) => JSON.stringify(cd))}
            journalHistory={
                props.registrations.length === 0 || props.registrations.length > 1
                    ? []
                    : allJournalEntries
            }
            alert={
                <Typography>
                    Please note: Any journal entries entered here will not display in IAS. This
                    feature is for proof of concept and user feedback purpose only. Journaling will
                    remain in IAS until this proof of concept is finalized, including templates for
                    journal entries. If you wish to provide any feedback please contact Andy Wilson
                    (<Link href="mailto:wilsona@ccl.org">wilsona@ccl.org</Link>).
                </Typography>
            }
            alertColor="error"
            open={props.open}
            onClose={() => props.onClose()}
            onSave={() => saveEntry()}
            onSaveError={(msg: string) => console.log(msg)}
        />
    );
};

export default RegistrationJournalDrawer;
